import React, { useEffect } from "react";
import "./Platform.css";

import Section from "../components/marketing/layout/Section";
import MarketingButton from "../components/marketing/actions/MarketingButton";
import ProductCanvas from "../components/marketing/ProductCanvas";
import ProductMomentCard from "../components/marketing/ProductMomentCard";
import ParallaxCard from "../components/motion/ParallaxCard";
import Reveal from "../components/motion/Reveal";


const sections = [
  {
    number: "01",
    eyebrow: "BRAND KIT",
    title: "Start with your brand.",
    outcome: "Create once. Stay consistent everywhere.",
    text:
      "Define the identity, voice, audience, and creative rules that guide every image, video, and campaign asset generated inside ADGen.",
    points: [
      "Upload your logo and brand assets",
      "Define colors, fonts, voice, and audience",
      "Set default creative preferences",
      "Apply your Brand Kit across generators",
    ],
    image: "/screenshots/brand-kit.png",
    imageAlt:
      "ADGen MCM Brand Kit showing brand strategy, colors, typography, logo, and a live brand preview",
    callouts: ["Brand identity", "Creative rules", "Live preview"],
    featured: true,
    visualTone: "violet",
  },
  {
    number: "02",
    eyebrow: "IMAGE GENERATION",
    title: "Turn ideas into campaign-ready creative.",
    outcome: "Go from campaign brief to polished ad creative.",
    text:
      "Create polished ads or custom visual concepts with as much—or as little—creative control as you need. Start fast with Quick Create, or control copy, branding, references, format, and creative direction in the full workspace.",
    points: [
      "Create in seconds with Quick Create",
      "Choose your copy, branding, and creative structure",
      "Use Brand Kits and reference images for stronger direction",
      "Create multiple formats, from structured ads to custom visual concepts",
    ],
    image: "/screenshots/image-generator.png",
    imageAlt:
      "ADGen MCM Image Generator showing campaign inputs, Brand Kit defaults, audience controls, and generated creative",
    callouts: ["Brand-aware", "Reference guided", "Multi-format"],
    reverse: true,
    visualTone: "blue",
  },
  {
    number: "03",
    eyebrow: "VIDEO GENERATION",
    title: "Create motion without the production bottleneck.",
    outcome: "Build campaign-ready video without a full production team.",
    text:
      "Turn written prompts or existing images into short-form video ads with brand context, flexible formats, voiceover, and creative direction.",
    points: [
      "Prompt-to-video generation",
      "Image-to-video generation",
      "Flexible durations and vertical formats",
      "Voiceover and audio workflows",
    ],
    image: "/screenshots/video-generator.png",
    imageAlt:
      "ADGen MCM Video Generator showing prompt-to-video settings, Brand Kit controls, voiceover, duration, and format options",
    callouts: ["Prompt to video", "Voiceover", "Vertical formats"],
    visualTone: "violet",
  },
  {
    number: "04",
    eyebrow: "CREATIVE OPTIMIZER",
    title: "Improve what is not working.",
    outcome: "Turn performance problems into stronger creative direction.",
    text:
      "Analyze an existing ad alongside its campaign context and performance metrics to identify weaknesses in messaging, creative structure, and execution—then generate stronger recommendations.",
    points: [
      "Import creatives directly from your Library",
      "Analyze Google Ads creative",
      "Analyze Meta Ads creative",
      "Identify performance and creative-structure weaknesses",
      "Generate optimized replacements",
    ],
    image: "/screenshots/optimizer.png",
    imageAlt:
      "ADGen MCM Creative Optimizer showing campaign context, performance inputs, audience temperature, and analysis guidance",
    callouts: ["Performance diagnosis", "Action plan", "Stronger variants"],
    reverse: true,
    visualTone: "blue",
  },
  {
    number: "05",
    eyebrow: "CREATIVE LIBRARY",
    title: "Keep every creative organized.",
    outcome: "Your complete creative history in one searchable workspace.",
    text:
      "Store generated images and videos alongside campaign context, performance data, winner labels, and creative history.",
    points: [
      "Organize image and video assets",
      "Search and filter creative history",
      "Track performance on individual assets",
      "Mark successful creative as winners",
    ],
    image: "/screenshots/creative-library.png",
    imageAlt:
      "ADGen MCM Creative Library showing generated image assets, video counts, performance tracking, filters, and winner labels",
    callouts: ["Searchable assets", "Performance data", "Winner tracking"],
    visualTone: "violet",
  },
  {
    number: "06",
    eyebrow: "CAMPAIGN REPORTING",
    title: "Understand campaign performance in one place.",
    outcome: "Connect your advertising accounts and monitor creative performance without switching between platforms.",
    text:
      "Track campaign performance with Google Ads and Meta Ads integrations, compare creative results, monitor key metrics, and keep reporting connected to the rest of your creative workflow.",
    points: [
      "Connect Google Ads",
      "Connect Meta Ads",
      "Unified reporting dashboard",
      "Monitor campaign metrics",
      "Compare creative performance",
      "Refresh live reporting",
    ],
    image: "/screenshots/reporting.png",
    imageAlt:
      "ADGen MCM Creative Library showing generated image assets, video counts, performance tracking, filters, and winner labels",
    callouts: ["Searchable assets", "Performance data", "Winner tracking"],
     reverse: true,
    visualTone: "violet",
  },
  {
    number: "07",
    eyebrow: "PERFORMANCE, CAMPAIGN INTELLIGENCE & Insights",
    title: "Turn qualified results into future creative guidance.",
    outcome: "Build a reusable Creative DNA profile for your brand.",
    text:
      "Performance Intelligence combines your Creative Library, Google Ads, Meta Ads, and manual performance tracking to build Creative DNA that continuously improves future image and video generations.",
    points: [
      "Connect Google, Meta Ads or add performance manually",
      "Qualify reliable winners and underperformers",
      "Build Creative DNA from colors, styles, copy, composition, and creative structure",
      "Apply learned patterns to future image and video generations",
      "Use Campaign Intelligence to optimize your running campaign to maximize reach and ROI.",
    ],
    image: "/screenshots/insights.png",
    imageAlt:
      "ADGen MCM Insights showing performance sources, creative intelligence score, weighted ROAS, CTR, and tracked creative",
    callouts: ["Creative DNA", "Qualified signals", "Generation guidance"],
    visualTone: "blue",
  },
  {
    number: "08",
    eyebrow: "CREATIVE STUDIO",
    title: "Perfect every creative before launch.",
    outcome: "Make final edits without leaving the platform.",
    text:
      "Refine generated assets with editable text, positioning controls, safe-area guides, font styling, and export-ready files.",
    points: [
      "Add and edit text layers",
      "Use platform-safe layout guides",
      "Adjust typography and alignment",
      "Export polished PNG creative",
    ],
    image: "/screenshots/creative-studio.png",
    imageAlt:
      "ADGen MCM Creative Studio showing a skincare ad canvas, editable text, typography controls, safe-area guides, and PNG export",
    callouts: ["Editable layers", "Safe-area guides", "Export ready"],
    featured: true,
    reverse: true,
    visualTone: "violet",
  },
];



function ScreenshotCallouts({ items }) {
  return (
    <div
      className="platform-screenshot-callouts"
      aria-label="Highlighted capabilities"
    >
      {items.map((item, index) => (
        <span
          key={item}
          className={`platform-screenshot-callout callout-${index + 1}`}
        >
          <i aria-hidden="true" />
          {item}
        </span>
      ))}
    </div>
  );
}

export default function Platform() {
    useEffect(() => {
      if (!window.location.hash) return;

      const id = window.location.hash.replace("#", "");

      const scrollToSection = () => {
        const element = document.getElementById(id);

        if (element) {
          element.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      };

      // Allows the page and Reveal components time to render.
      const timeout = window.setTimeout(scrollToSection, 300);

      return () => window.clearTimeout(timeout);
    }, []);

  return (
    <main className="platform-page platform-v2">
      <Section
        size="xl"
        container="wide"
        align="center"
        className="platform-v2-hero"
      >
        <div className="platform-v2-hero-bg" />

        <div className="platform-v2-hero-content">
          <p className="platform-v2-eyebrow">Platform</p>

          <h1>
            <span className="platform-v2-line platform-v2-line-primary">
              Connect the complete
            </span>

            <span className="platform-v2-line platform-v2-line-secondary">
              creative workflow.
            </span>
          </h1>

          <p className="platform-v2-description">
            Build your brand foundation, generate image and video creative,
            refine and organize assets, then use qualified campaign results to
            guide future generations.
          </p>

          <div className="platform-v2-actions">
            <MarketingButton href="/subscribe" size="lg">
              Start creating
            </MarketingButton>

            <MarketingButton
              href="/pricing"
              size="lg"
              variant="secondary"
            >
              View pricing
            </MarketingButton>
          </div>
        </div>

        <div className="platform-v2-hero-visual">
          <ParallaxCard>
            <ProductCanvas
              src="/screenshots/dashboard.png"
              alt="ADGen MCM dashboard showing image usage, video usage, Brand Kit status, current plan, and quick creative actions"
              variant="hero"
              className="platform-v2-dashboard-canvas"
              floatingCards={
                <>
                  <ProductMomentCard
                    position="top-left"
                    status="success"
                    icon="brand"
                    eyebrow="Brand Kit"
                    title="Brand ready"
                    detail="Colors, fonts, and voice connected"
                  />

                  <ProductMomentCard
                    position="top-right"
                    status="active"
                    icon="spark"
                    eyebrow="Generation"
                    title="Creative workflow"
                    detail="Images, video, and copy in one place"
                  />

                  <ProductMomentCard
                    position="bottom-left"
                    status="insight"
                    icon="chart"
                    eyebrow="Optimizer"
                    title="Performance guided"
                    detail="Turn campaign data into stronger creative"
                  />

                  <ProductMomentCard
                    position="bottom-right"
                    status="video"
                    icon="video"
                    eyebrow="Video"
                    title="Video ready"
                    detail="Campaign-ready motion creative"
                  />
                </>
              }
            />
          </ParallaxCard>
        </div>

        <div
          className="platform-v2-capabilities"
          aria-label="Platform capabilities"
        >
          <span>Brand-aware generation</span>
          <i />
          <span>Image, video, and copy</span>
          <i />
          <span>Performance Intelligence</span>
        </div>
      </Section>

      <section className="platform-story-intro">
        <div className="platform-container">
          <Reveal>
            <div className="platform-story-intro-inner">
              <span className="platform-story-intro-label">
                One connected workflow
              </span>

              <h2>
                Move from brand direction to generation, measurement, and improvement.
              </h2>

              <p>
                ADGen connects the stages that normally live in separate tools,
                giving your team one continuous path from idea to performance
                learning.
              </p>

              <div
                className="platform-story-steps"
                aria-label="ADGen platform workflow"
              >
                <span>Define</span>
                <i aria-hidden="true" />
                <span>Generate</span>
                <i aria-hidden="true" />
                <span>Refine</span>
                <i aria-hidden="true" />
                <span>Measure</span>
                <i aria-hidden="true" />
                <span>Improve</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <div className="platform-product-story">
        {sections.map((section, index) => (
          <React.Fragment key={section.title}>
            <Reveal delay={100}>
              <section
              className={[
                "platform-showcase",
                section.reverse ? "reverse" : "",
                section.featured ? "featured" : "",
                `tone-${section.visualTone}`,
              ]
                .filter(Boolean)
                .join(" ")}
              data-section-number={section.number}
            >
              <div className="platform-showcase-light" aria-hidden="true" />

              <div className="platform-container platform-showcase-grid">
                <div className="platform-showcase-copy">
                  <div className="platform-showcase-meta">
                    <span className="platform-section-number">
                      {section.number}
                    </span>

                    <span className="platform-pill">
                      {section.eyebrow}
                    </span>
                  </div>

                  <h2>{section.title}</h2>

                  <p className="platform-showcase-outcome">
                    {section.outcome}
                  </p>

                  <p className="platform-showcase-description">
                    {section.text}
                  </p>

                  <ul>
                    {section.points.map((point) => (
                      <li key={point}>
                        <span aria-hidden="true">✓</span>
                        {point}
                      </li>
                    ))}
                  </ul>

                  {(section.number === "02" || section.number === "03") && (
                    <MarketingButton
                      href="/examples"
                      size="md"
                      variant="secondary"
                      className="platform-examples-link"
                    >
                      See generated examples
                    </MarketingButton>
                  )}

                  <div className="platform-showcase-progress">
                    <span>
                      {String(index + 1).padStart(2, "0")}
                    </span>

                    <div>
                      <i
                        style={{
                          width: `${
                            ((index + 1) / sections.length) * 100
                          }%`,
                        }}
                      />
                    </div>

                    <span>
                      {String(sections.length).padStart(2, "0")}
                    </span>
                  </div>
                </div>

                <div className="platform-showcase-visual">
                  <div className="platform-screenshot-shell">
                    <div className="platform-screenshot-glow" />

                    <div className="platform-screenshot-frame">
                      <img
                        src={section.image}
                        alt={section.imageAlt}
                        className="platform-screenshot"
                        loading="lazy"
                        decoding="async"
                      />

                      <div
                        className="platform-screenshot-shine"
                        aria-hidden="true"
                      />
                    </div>

                    <ScreenshotCallouts items={section.callouts} />
                  </div>
                </div>
              </div>
              </section>
            </Reveal>
          </React.Fragment>
        ))}
      </div>

      <section className="platform-final">
        <div className="platform-container platform-final-inner">
          <Reveal delay={100}>
            <div className="platform-final-content">
              <span className="platform-pill">
                Ready to build better ads?
              </span>

              <h2>
                Bring the entire creative workflow into one platform.
              </h2>

              <p>
                Generate branded image ads, video creative, and campaign copy,
                then use qualified results to build Creative DNA and guide future
                generations without stitching together separate tools.
              </p>

              <div className="platform-final-proof">
                <span>
                  <i aria-hidden="true" />
                  Brand-aware creative
                </span>

                <span>
                  <i aria-hidden="true" />
                  Image and video generation
                </span>

                <span>
                  <i aria-hidden="true" />
                  Creative that learns from performance
                </span>
              </div>

              <div className="platform-final-actions">
                <MarketingButton
                  href="/subscribe"
                  size="lg"
                  className="platform-final-primary"
                >
                  Start creating
                </MarketingButton>

                <MarketingButton
                  href="/pricing"
                  size="lg"
                  variant="secondary"
                  className="platform-final-secondary"
                >
                  View pricing
                </MarketingButton>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </main>
  );
}